import React, { useContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { Autocomplete, Chip, TextField, Tooltip } from "@mui/material";

import { Reference } from "../../../api/types/references"; // Import the Reference type
import { useAssignedReferences } from "../../../hooks/useAssignedReferences";
import { useAuth } from "../../../hooks/useAuth";
import { useAllReferencesList } from "../api/useAllReferencesList";
import { useReferencesAssign } from "../api/useReferencesAssign";
import { useReferencesUnassign } from "../api/useReferencesUnassign";
import { CafsContext } from "../CafsContext";
import { CafsSection } from "../const/cafs-section";

export type ReferenceAssignProps = {
  fieldName: string;
  sectionName: CafsSection;
  productCode: string | undefined;
  guestName: string | undefined | null;
  hideInheritedReferences?: boolean;
};

export function ReferenceAssign({
  fieldName,
  sectionName,
  productCode,
  guestName,
  hideInheritedReferences = false,
}: ReferenceAssignProps) {
  const { isFarmTreeRole } = useAuth();
  const [selectedReferences, setSelectedReferences] = useState<Reference[]>([]);
  const [readableInheritedReferences, setReadableInheritedReferences] =
    useState<Reference[]>([]);

  const { isReadMode } = useContext(CafsContext);
  const { assignReference } = useReferencesAssign(
    sectionName,
    productCode,
    guestName,
  );
  const { unassignReference } = useReferencesUnassign();
  const { allReferencesList, isFetching } = useAllReferencesList();
  const { assignedReferences, inheritedReferences } = useAssignedReferences(
    fieldName,
    productCode,
    sectionName,
  );

  useEffect(() => {
    if (allReferencesList && assignedReferences) {
      const assignedRefs = allReferencesList.filter(ref =>
        assignedReferences.some(
          assignedRef => assignedRef.ReferenceId === ref.Id,
        ),
      );
      const inheritedRefs = allReferencesList.filter(ref =>
        inheritedReferences.some(
          inheritedRef => inheritedRef.ReferenceId === ref.Id,
        ),
      );
      setSelectedReferences(assignedRefs);
      setReadableInheritedReferences(inheritedRefs);
    }
  }, [assignedReferences, inheritedReferences, allReferencesList]);

  const handleReferenceUnassign = (itemId: number | undefined | null) => {
    const connectionId = assignedReferences?.find(
      r => r.ReferenceId === itemId,
    );
    unassignReference({ id: connectionId?.Id! });
  };

  const handleReferenceChange = (
    e: React.SyntheticEvent<Element, Event>,
    newValue: Reference[],
  ) => {
    let changedItem;
    let prevValue = selectedReferences;

    if (newValue.length > prevValue.length) {
      changedItem = newValue.find(
        item => !prevValue.some(prevItem => prevItem.Id === item.Id),
      );
      assignReference({
        referenceId: changedItem?.Id!,
        name: fieldName,
      });
    } else {
      changedItem = prevValue.find(
        prevItem => !newValue.some(item => item.Id === prevItem.Id),
      );
      handleReferenceUnassign(changedItem?.Id);
    }
  };

  const { formatMessage } = useIntl();
  return (
    <>
      <Autocomplete
        size="small"
        disableClearable
        multiple
        disabled={isReadMode || isFetching || !isFarmTreeRole}
        value={selectedReferences}
        onChange={handleReferenceChange}
        options={allReferencesList || []}
        getOptionLabel={option => option?.FullName ?? ""}
        getOptionKey={option => option?.Id?.toString() ?? ""}
        renderTags={(value, getTagProps) =>
          [
            ...value.map((option, index) => (
              <div key={option.Id}>
                <Tooltip title={option.FullName}>
                  <Chip
                    {...getTagProps({ index })}
                    key={option.Id}
                    label={option.Id}
                    style={{ maxWidth: "100%" }}
                  />
                </Tooltip>
              </div>
            )),
          ].filter(Boolean)
        }
        renderInput={params => (
          <TextField
            {...params}
            label={formatMessage({
              id: "CAFS.REFERENCE_ASSIGN_LABEL",
              defaultMessage: "Assigned references",
            })}
          />
        )}
      />
      {!hideInheritedReferences && readableInheritedReferences.length > 0 && (
        <div className="mt-2">
          <div className="text-sm font-bold">Parent references:</div>
          <div className="flex flex-wrap gap-2 mt-2">
            {readableInheritedReferences.map(ref => (
              <Tooltip key={ref.Id} title={`${ref.Id}. ${ref.FullName}`}>
                <Chip label={ref.Id} />
              </Tooltip>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
